(() => {
  if (window.cartDrawerRecommendationsInitialized) return;

  window.cartDrawerRecommendationsInitialized = true;

  const HOST_SELECTOR =
    '[data-cart-recommendations-host]';

  const SHOP_ROOT =
    window.Shopify?.routes?.root || '/';

  const CART_ADD_URL =
    `${SHOP_ROOT}cart/add.js`;

  const getHost = () => {
    return document.querySelector(HOST_SELECTOR);
  };

  const getMiniCart = () => {
    return document.querySelector('mini-cart');
  };

  function getRecommendationsContainer(element) {
    return element?.closest(
      '[data-cart-drawer-recommendations]'
    );
  }

  function updateNavigation(container, page) {
    if (!container) return;

    const normalizedPage =
      page === 1 ? 1 : 0;

    container.dataset.currentPage =
      String(normalizedPage);

    container
      .querySelectorAll(
        '[data-recommendations-dot]'
      )
      .forEach((dot) => {
        const dotPage = Number(
          dot.dataset.recommendationsDot
        );

        dot.classList.toggle(
          'active',
          dotPage === normalizedPage
        );

        dot.setAttribute(
          'aria-current',
          dotPage === normalizedPage
            ? 'true'
            : 'false'
        );
      });
  }

  function showRecommendationPage(
    container,
    requestedPage
  ) {
    if (!container) return;

    const viewport = container.querySelector(
      '.cart-drawer-recommendations__viewport'
    );

    if (!viewport) return;

    const page =
      requestedPage === 1 ? 1 : 0;

    viewport.scrollTo({
      left: page * viewport.clientWidth,
      behavior: 'smooth'
    });

    updateNavigation(container, page);
  }

  function resetRecommendationCarousel(scope) {
    const container = scope.querySelector(
      '[data-cart-drawer-recommendations]'
    );

    if (!container) return;

    const viewport = container.querySelector(
      '.cart-drawer-recommendations__viewport'
    );

    if (viewport) {
      viewport.scrollLeft = 0;
    }

    updateNavigation(container, 0);
  }

  async function loadCartRecommendations({
    force = false
  } = {}) {
    const host = getHost();

    if (!host) return;

    const productId =
      host.dataset.productId?.trim();

    if (
      !productId ||
      host.classList.contains('hidden')
    ) {
      host.innerHTML = '';
      delete host.dataset.loadedProductId;
      return;
    }

    if (
      !force &&
      host.dataset.loadedProductId === productId &&
      host.innerHTML.trim()
    ) {
      return;
    }

    if (host.dataset.loading === 'true') {
      return;
    }

    host.dataset.loading = 'true';
    host.setAttribute('aria-busy', 'true');

    try {
      const parameters = new URLSearchParams({
        section_id:
          'cart-drawer-recommendations',
        product_id: productId,
        limit: '8',
        intent: 'related'
      });

      const response = await fetch(
        `${SHOP_ROOT}recommendations/products?${parameters.toString()}`,
        {
          method: 'GET',
          headers: {
            Accept: 'text/html'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Recommendations request failed: ${response.status}`
        );
      }

      const sectionHTML =
        await response.text();

      const parsedDocument =
        new DOMParser().parseFromString(
          sectionHTML,
          'text/html'
        );

      const recommendations =
        parsedDocument.querySelector(
          '[data-cart-drawer-recommendations]'
        );

      host.innerHTML = recommendations
        ? recommendations.outerHTML
        : '';

      host.dataset.loadedProductId =
        productId;

      host.classList.toggle(
        'hidden',
        !recommendations
      );

      resetRecommendationCarousel(host);
    } catch (error) {
      console.error(
        'Cart recommendations error:',
        error
      );

      host.innerHTML = '';
    } finally {
      delete host.dataset.loading;
      host.removeAttribute('aria-busy');
    }
  }

  function syncRecommendationsWithCart(cart) {
    const host = getHost();

    if (!host) return;

    const firstProductId =
      cart?.items?.[0]?.product_id || '';

    host.dataset.productId =
      firstProductId;

    host.classList.toggle(
      'hidden',
      !firstProductId
    );

    if (!firstProductId) {
      host.innerHTML = '';
      delete host.dataset.loadedProductId;
      return;
    }

    loadCartRecommendations({
      force: true
    });
  }

  async function addRecommendedProduct(button) {
    const variantId = Number(
      button.dataset.variantId
    );

    if (!variantId || button.disabled) return;

    const originalText =
      button.textContent.trim();

    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding...';

    try {
      const response = await fetch(
        CART_ADD_URL,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1
              }
            ]
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          `Add to cart failed: ${response.status}`
        );
      }

      button.textContent = 'Added';

      const miniCart = getMiniCart();

      if (
        miniCart &&
        typeof miniCart.update === 'function'
      ) {
        await miniCart.update();
      }

      window.setTimeout(() => {
        button.textContent = originalText;
      }, 900);
    } catch (error) {
      console.error(
        'Recommended product error:',
        error
      );

      button.textContent = 'Try again';
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.classList.remove('is-loading');
      }, 900);
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const addButton = event.target.closest(
        '[data-recommendation-add]'
      );

      if (addButton) {
        event.preventDefault();
        addRecommendedProduct(addButton);
        return;
      }

      const previousButton =
        event.target.closest(
          '[data-recommendations-previous]'
        );

      if (previousButton) {
        event.preventDefault();

        showRecommendationPage(
          getRecommendationsContainer(
            previousButton
          ),
          0
        );

        return;
      }

      const nextButton =
        event.target.closest(
          '[data-recommendations-next]'
        );

      if (nextButton) {
        event.preventDefault();

        showRecommendationPage(
          getRecommendationsContainer(
            nextButton
          ),
          1
        );

        return;
      }

      const dotButton = event.target.closest(
        '[data-recommendations-dot]'
      );

      if (dotButton) {
        event.preventDefault();

        showRecommendationPage(
          getRecommendationsContainer(
            dotButton
          ),
          Number(
            dotButton.dataset.recommendationsDot
          )
        );
      }
    }
  );

  document.addEventListener(
    'minicart:updated',
    (event) => {
      syncRecommendationsWithCart(
        event.detail?.cart
      );
    }
  );

  const initialize = () => {
    loadCartRecommendations();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();